import {
  BarPlot,
  type BarSeriesType,
  ChartsContainer,
  ChartsAxisHighlight,
  ChartsGrid,
  ChartsXAxis,
  ChartsYAxis,
  LinePlot,
  type LineSeriesType,
} from "@mui/x-charts";
import { useMemo } from "react";
import { useSelector } from "react-redux";

import { RootState } from "../../../../redux/store";
import { CashflowSeriesPoint } from "../../../../services/transactions/types";
import { getThemeColors } from "../../../../utils/constants";
import { CashflowTooltip } from "./CashflowTooltip";
import { CONTEXT_BAR_OPACITY, NET_LINE_COLOR } from "./constants";
import { CashflowChartProps, InsightsBucketSlot } from "./types";
import { ChartWrap } from "./styles";

const CHART_HEIGHT = 300;
const X_AXIS_ID = "cashflow-x";
const Y_AXIS_ID = "cashflow-y";

export interface CashflowDatasets {
  labels: string[];
  incomeContext: (number | null)[];
  incomeSelected: (number | null)[];
  expenseContext: (number | null)[];
  expenseSelected: (number | null)[];
  net: number[];
}

const slotsFromSeries = (series: CashflowSeriesPoint[]): InsightsBucketSlot[] =>
  series.map((point) => ({
    key: point.bucket,
    label: point.bucket,
    inSelection: true,
  }));

export const buildCashflowDatasets = (
  slots: InsightsBucketSlot[],
  series: CashflowSeriesPoint[],
  showIncome: boolean,
  showExpense: boolean,
): CashflowDatasets => {
  const effectiveSlots = slots.length > 0 ? slots : slotsFromSeries(series);
  const byBucket = new Map(series.map((point) => [point.bucket, point]));
  const labels: string[] = [];
  const incomeContext: (number | null)[] = [];
  const incomeSelected: (number | null)[] = [];
  const expenseContext: (number | null)[] = [];
  const expenseSelected: (number | null)[] = [];
  const net: number[] = [];

  for (const slot of effectiveSlots) {
    const point = byBucket.get(slot.key);
    const income = showIncome ? (point?.income ?? 0) : 0;
    const expense = showExpense ? (point?.expense ?? 0) : 0;
    labels.push(slot.label);
    incomeContext.push(slot.inSelection ? null : income);
    incomeSelected.push(slot.inSelection ? income : null);
    expenseContext.push(slot.inSelection ? null : expense);
    expenseSelected.push(slot.inSelection ? expense : null);
    net.push(income - expense);
  }

  return {
    labels,
    incomeContext,
    incomeSelected,
    expenseContext,
    expenseSelected,
    net,
  };
};

const dim = (color: string): string =>
  `color-mix(in srgb, ${color} ${CONTEXT_BAR_OPACITY * 100}%, transparent)`;

export const CashflowChart: React.FC<CashflowChartProps> = ({
  slots,
  series,
  showIncome,
  showExpense,
}) => {
  const theme = useSelector((state: RootState) => state.theme);
  const colors = getThemeColors(theme.theme);

  const data = useMemo(
    () => buildCashflowDatasets(slots, series, showIncome, showExpense),
    [slots, series, showIncome, showExpense],
  );

  const incomeColor = colors.statusSuccess;
  const expenseColor = colors.statusError;

  const chartSeries = useMemo(() => {
    const result: (BarSeriesType | LineSeriesType)[] = [];
    if (showIncome) {
      result.push(
        {
          type: "bar",
          id: "income-selected",
          data: data.incomeSelected,
          color: incomeColor,
          label: "Income",
          stack: "income",
        },
        {
          type: "bar",
          id: "income-context",
          data: data.incomeContext,
          color: dim(incomeColor),
          label: "Income",
          stack: "income",
        },
      );
    }
    if (showExpense) {
      result.push(
        {
          type: "bar",
          id: "expense-selected",
          data: data.expenseSelected,
          color: expenseColor,
          label: "Expenses",
          stack: "expense",
        },
        {
          type: "bar",
          id: "expense-context",
          data: data.expenseContext,
          color: dim(expenseColor),
          label: "Expenses",
          stack: "expense",
        },
      );
    }
    result.push({
      type: "line",
      id: "net",
      data: data.net,
      color: NET_LINE_COLOR,
      label: "Net",
      curve: "linear",
      showMark: false,
    });
    return result;
  }, [data, showIncome, showExpense, incomeColor, expenseColor]);

  const chartSx = useMemo(
    () => ({
      "& .MuiLineChart-line": {
        strokeDasharray: "6 4",
        strokeWidth: 2,
      },
      "& .MuiChartsAxis-tickLabel": {
        fill: "var(--color-text-default)",
      },
      "& .MuiChartsAxis-line": {
        stroke: "var(--color-text-default)",
      },
      "& .MuiChartsAxis-tick": {
        stroke: "var(--color-text-default)",
      },
    }),
    [],
  );

  return (
    <ChartWrap>
      <ChartsContainer
        role="img"
        aria-label="Cashflow by period: income, expenses and net"
        height={CHART_HEIGHT}
        series={chartSeries}
        xAxis={[{ id: X_AXIS_ID, scaleType: "band", data: data.labels }]}
        yAxis={[{ id: Y_AXIS_ID }]}
        sx={chartSx}
      >
        <ChartsGrid horizontal />
        <BarPlot />
        <LinePlot />
        <ChartsXAxis axisId={X_AXIS_ID} />
        <ChartsYAxis axisId={Y_AXIS_ID} />
        <ChartsAxisHighlight x="band" />
        <CashflowTooltip />
      </ChartsContainer>
    </ChartWrap>
  );
};
